import { LOGO_URL, NAV_LINKS, CONTACT, DEALERSHIP_URL } from "@/constants/site";
import { Phone, Mail, MapPin } from "lucide-react";
import { useScrollTo } from "@/lib/motion";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { getLocaleFromPath, getLocalizedPath } from "@/lib/locale";

export const Footer = ({ content = {} }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const scrollTo = useScrollTo();
  const year = new Date().getFullYear();
  const contact = { ...CONTACT, ...(content.contact || {}) };
  const home = content.home || {};
  const links = home.nav_links?.length ? home.nav_links : NAV_LINKS;
  const locale = getLocaleFromPath(location.pathname) || "tr";
  const homePath = getLocalizedPath("/", locale);
  const isHomeRoute = location.pathname === homePath || location.pathname === `${homePath}/`;

  return (
    <footer data-testid="site-footer" className="bg-surface border-t border-detail">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <img src={LOGO_URL} alt="Nolena Global" className="h-10 w-auto" />
            <p className="mt-5 max-w-sm text-sm text-ink/65 leading-relaxed">
              {home.footer_description || "Dostlarımız için sevgiyle üretiyoruz. Kaliteli üretim, güvenilir iş ortaklığı ve Türkiye’ye yayılmış bayi ağı."}
            </p>
            <a
              href={DEALERSHIP_URL}
              data-testid="footer-dealership-cta"
              className="mt-6 inline-flex items-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-cream hover:bg-brand2 transition-colors"
            >
              {t("common.apply")}
            </a>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-heading font-semibold text-ink text-sm uppercase tracking-wider">
              {t("footer.menu")}
            </h4>
            <ul className="mt-5 space-y-3">
                  {links.map((link) => <li key={link.target}>
                    {isHomeRoute ? <a href={link.target} onClick={(e) => { e.preventDefault(); scrollTo(link.target); }} data-testid={`footer-link-${link.target.replace("#", "")}`} className="text-sm text-ink/70 hover:text-brand transition-colors">{link.label}</a> : <Link to={`${homePath}${link.target}`} data-testid={`footer-link-${link.target.replace("#", "")}`} className="text-sm text-ink/70 hover:text-brand transition-colors">{link.label}</Link>}
                  </li>)}
              <li><Link to={getLocalizedPath("/sertifikalar", locale)} className="text-sm text-ink/70 transition-colors hover:text-brand">{t("documents.certificatesLink")}</Link></li>
              <li><Link to={getLocalizedPath("/kataloglar", locale)} className="text-sm text-ink/70 transition-colors hover:text-brand">{t("documents.catalogsLink")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-heading font-semibold text-ink text-sm uppercase tracking-wider">
              {t("footer.contact")}
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-ink/70">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-brand shrink-0" />
                <a href={contact.phone_href || contact.phoneHref} className="hover:text-brand transition-colors">
                  {contact.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-brand shrink-0" />
                <a href={`mailto:${contact.email}`} className="hover:text-brand transition-colors">
                  {contact.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                <span>{contact.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-detail flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-ink/55">
            © {year} Nolena Global. {t("footer.rights")}
          </p>
          <p className="text-sm text-ink/55">{t("footer.motto")}</p>
        </div>
      </div>
    </footer>
  );
};
